import React, { useRef, useState, useCallback, useEffect } from 'react';
import { config } from "../Const.js";
import {
    CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Box, TableSortLabel, Button, Dialog, Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { generateImageUrlFromCDN, executeFetch } from '../Util.js';
import SearchToolbar from './SearchToolbar.jsx';

const STORAGE_KEY = "quiz_manager_last_search";

function QuizManager(props) {
    const handleLoadQuizClick = props.onLoadQuiz;

    const [quizList, setQuizList] = useState([]);
    const [order, setOrder] = useState('desc');
    const [loading, setLoading] = useState(false);
    const [orderBy, setOrderBy] = useState('modifyDate');
    const [hasLoadedAnything, setHasLoadedAnything] = useState(false);

    const [deleteWarningPopupOpen, setDeleteWarningPopupOpen] = useState(false);
    const [selectedQuizForDelete, setSelectedQuizForDelete] = useState(null);

    // Track search term in a ref
    const currentSearchTermRef = useRef(localStorage.getItem(STORAGE_KEY) || '');
    const inFlightRef = useRef(false);

    const ORDER_ASC = 'asc';
    const ORDER_DESC = 'desc';

    // Styled Components
    const StyledTableRow = styled(TableRow)(({ theme }) => ({
        '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
        '&:last-child td, &:last-child th': { border: 0 },
    }));

    const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
        backgroundColor: '#1565c0',
        color: 'white',
    }));

    const StyledTableSortLabel = styled(TableSortLabel)(({ theme }) => ({
        '&.MuiTableSortLabel-root': { color: 'white' },
        '&.MuiTableSortLabel-root.Mui-active': { color: 'white' },
        '& .MuiTableSortLabel-icon': { color: 'white !important' }
    }));

    const roundToDecimalPlaces = (number, decimalPlaces) => {
        const factor = Math.pow(10, decimalPlaces);
        return Math.round(number * factor) / factor;
    };

    // Sorting logic (No useMemo)
    const getSortedList = () => {
        if (!quizList || quizList.length === 0) return [];
        return [...quizList].sort((a, b) => {
            let valueA, valueB;
            switch (orderBy) {
                case 'label':
                    valueA = (a?.label || '').toUpperCase();
                    valueB = (b?.label || '').toUpperCase();
                    break;
                case 'users':
                    valueA = a?.quizStats?.userCount ?? 0;
                    valueB = b?.quizStats?.userCount ?? 0;
                    break;
                case 'modifyDate':
                default:
                    valueA = a?.modifyDate || '';
                    valueB = b?.modifyDate || '';
                    break;
            }
            let result = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
            return order === ORDER_DESC ? result * -1 : result;
        });
    };

    const sortedQuizList = getSortedList();

    const fetchQuizList = useCallback((search = '') => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        setLoading(true);

        const trimmedSearch = (search || '').trim();
        currentSearchTermRef.current = trimmedSearch;

        // Persistence logic: Store if search exists, clear if empty (Load All)
        if (trimmedSearch) {
            localStorage.setItem(STORAGE_KEY, trimmedSearch);
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }

        let url = config.SERVER_URL + "/api/v1/admin/quiz/with-stats";
        if (trimmedSearch.length > 0) {
            url += `?search=${encodeURIComponent(trimmedSearch)}`;
        }

        const responseHandler = (result) => {
            setQuizList(Array.isArray(result) ? result : []);
            setLoading(false);
            setHasLoadedAnything(true);
            inFlightRef.current = false;
        };

        const errorHandler = () => {
            setLoading(false);
            setHasLoadedAnything(true);
            inFlightRef.current = false;
        };

        executeFetch(url, "get", null, null, null, responseHandler, errorHandler);
    }, []);

    // Load initial search ONLY if keyword exists in storage
    useEffect(() => {
        const initialKeyword = localStorage.getItem(STORAGE_KEY);
        if (initialKeyword) {
            fetchQuizList(initialKeyword);
        }
    }, [fetchQuizList]);

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === ORDER_ASC;
        setOrder(isAsc ? ORDER_DESC : ORDER_ASC);
        setOrderBy(property);
    };

    const handleDeleteQuizClick = (quiz) => {
        setDeleteWarningPopupOpen(true);
        setSelectedQuizForDelete(quiz);
    };

    const closeDeleteWarningModal = () => {
        setDeleteWarningPopupOpen(false);
    };

    const handleDeleteQuizDeleteClicked = () => {
        if (!selectedQuizForDelete) return;
        const url = config.SERVER_URL + "/api/v1/admin/quiz/" + selectedQuizForDelete.id;
        const responseHandler = () => {
            closeDeleteWarningModal();
            fetchQuizList(currentSearchTermRef.current);
        };
        executeFetch(url, "delete", null, null, null, responseHandler);
    };

    // const emptyRows = 7 - (Math.min(sortedQuizList.length, 10));

    return (
        <Box sx={{ height: 800, overflowY: 'scroll' }}>
            <SearchToolbar
                onSearch={(text) => fetchQuizList(text)}
                onReset={() => fetchQuizList('')}
                loading={loading}
                hasLoadedAnything={hasLoadedAnything}
                // Requirement 3: Pass stored keyword to the Toolbar as initial value
                initialValue={localStorage.getItem(STORAGE_KEY) || ''}
            />

            {loading && (
                <Box sx={{ padding: '40px' }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && hasLoadedAnything && (
                <TableContainer sx={{ mt: 1 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <StyledTableHeaderCell sx={{ width: 100 }} />
                                <StyledTableHeaderCell sx={{ width: 350 }}>
                                    <StyledTableSortLabel
                                        active={orderBy === 'label'}
                                        direction={orderBy === 'label' ? order : 'asc'}
                                        onClick={() => handleSort('label')}
                                    >
                                        Label
                                    </StyledTableSortLabel>
                                </StyledTableHeaderCell>
                                <StyledTableHeaderCell sx={{ width: 100 }}>Published</StyledTableHeaderCell>
                                <StyledTableHeaderCell sx={{ width: 250 }}>Tags</StyledTableHeaderCell>
                                <StyledTableHeaderCell sx={{ width: 150 }}>
                                    <StyledTableSortLabel
                                        active={orderBy === 'modifyDate'}
                                        direction={orderBy === 'modifyDate' ? order : 'asc'}
                                        onClick={() => handleSort('modifyDate')}
                                    >
                                        Modify Date
                                    </StyledTableSortLabel>
                                </StyledTableHeaderCell>
                                <StyledTableHeaderCell sx={{ width: 60 }}>
                                    <StyledTableSortLabel
                                        active={orderBy === 'users'}
                                        direction={orderBy === 'users' ? order : 'desc'}
                                        onClick={() => handleSort('users')}
                                    >
                                        Users
                                    </StyledTableSortLabel>
                                </StyledTableHeaderCell>
                                <StyledTableHeaderCell sx={{ width: 100 }}>Avg Score</StyledTableHeaderCell>
                                <StyledTableHeaderCell sx={{ width: 120 }}>% Max Score</StyledTableHeaderCell>
                                <StyledTableHeaderCell sx={{ width: 130 }} />
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {sortedQuizList.map((quiz) => {
                                const tagList = quiz.tagList || [];
                                const modifyDate = quiz.modifyDate || '';
                                const stats = quiz.quizStats;
                                const avgScorePct = stats?.totalQuestionCount
                                    ? (stats.totalCorrectAnswerCount * 100) / stats.totalQuestionCount
                                    : 0;
                                const maxScorePct = stats?.userCount
                                    ? (stats.allCorrectCount * 100) / stats.userCount
                                    : 0;

                                return (
                                    <StyledTableRow key={quiz.id}>
                                        <TableCell>
                                            <img
                                                alt=""
                                                style={{ borderRadius: '10%', width: 80 }}
                                                src={generateImageUrlFromCDN(quiz.shareImageMetadata)}
                                            />
                                        </TableCell>
                                        <TableCell>{quiz.label}</TableCell>
                                        <TableCell>
                                            <Typography sx={{ color: quiz.published ? 'green' : 'red', fontWeight: 'bold' }}>
                                                {quiz.published ? 'Yes' : 'No'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {tagList.map(t => t.label).join(", ")}
                                        </TableCell>
                                        <TableCell>
                                            {modifyDate ? `${modifyDate.substr(0, 10)} ${modifyDate.substr(11, 5)}` : ''}
                                        </TableCell>
                                        <TableCell>{stats?.userCount ?? 0}</TableCell>
                                        <TableCell>%{roundToDecimalPlaces(avgScorePct, 1)}</TableCell>
                                        <TableCell>%{roundToDecimalPlaces(maxScorePct, 1)}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: '10px' }}>
                                                <Button size="small" variant="contained" onClick={() => handleLoadQuizClick(quiz)}>Load</Button>
                                                <Button size="small" variant="contained" color="error" onClick={() => handleDeleteQuizClick(quiz)}>Delete</Button>
                                            </Box>
                                        </TableCell>
                                    </StyledTableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={deleteWarningPopupOpen} onClose={closeDeleteWarningModal} PaperProps={{ sx: { width: 350, height: 250 } }}>
                <Box sx={{ display: 'flex', height: '100%', flexDirection: 'column', p: 3 }}>
                    <Typography variant="h5">Delete</Typography>
                    <Box sx={{ flexGrow: 1, mt: 2 }}>
                        <Typography variant="subtitle1">
                            Are you sure you want to delete '{selectedQuizForDelete?.label}'?
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button size="small" variant="outlined" onClick={closeDeleteWarningModal}>Cancel</Button>
                        <Button size="small" variant="contained" color="error" onClick={handleDeleteQuizDeleteClicked}>Delete</Button>
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
}

export default QuizManager;